import { DateTime } from '@ksyos/date-time';
import { Card, Table, TableBody, TableContainer, TableHead, TableHeader, TableRow } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IServiceActivity } from '@common/interfaces';
import type { CompensationPayoutLog } from '@common/interfaces/compensationPayoutLogs';
import type {
    IProvidedServicesAndCompensationPayouts,
    IServiceOrganizationSettings,
} from '@frontend/components/interfaces';
import { getLanguage } from '@common/translate/translate';
import { useAbortableFetch } from '@frontend/utils';
import type { EpisodeCategory } from '../../../../../common/enums/episodeCategory';
import { ServiceActivityCompensationPayoutsLogEntry } from './ServiceActivityCompensationPayoutLogEntry';

interface ServiceActivityCompensationPayoutsHistoryProps {
    employmentId?: string;
    episodeCategory: EpisodeCategory;
    organizationId: string;
    serviceActivity: IServiceActivity;
    serviceEmployments: IProvidedServicesAndCompensationPayouts['serviceEmployments'];
    serviceOrganizationSettings: IServiceOrganizationSettings[];
}
export function ServiceActivityCompensationPayoutsHistoryEntry(
    props: Readonly<ServiceActivityCompensationPayoutsHistoryProps>
) {
    const { employmentId, organizationId, serviceActivity, serviceEmployments, serviceOrganizationSettings } = props;
    const translate = getLanguage();
    const abortableFetch = useAbortableFetch();
    const serviceEmploymentIds = serviceEmployments.map((se) => se.id);
    const serviceOrganizationSettingIds = serviceOrganizationSettings.map((sos) => sos.id);
    const serviceGroupSettings = serviceOrganizationSettings.map((sos) => sos.serviceOrganizationGroupHistory).flat();

    const serviceGroupSettingIds = [...new Set(serviceGroupSettings.map((sogh) => sogh.serviceGroupSettingId))];

    const [compensationHistoryLogs, setCompensationHistoryLogs] = useState<CompensationPayoutLog[]>([]);

    useEffect(() => {
        getCompensationHistoryLogs();
    }, []);

    return (
        <Card key={serviceActivity.id}>
            <Card.Header title={`Logboek ${serviceActivity.name} - ${serviceEmployments[0].service.name}`} />
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translate.compensationHistoryHeaders.modificationDate}</TableHeader>
                            <TableHeader>{translate.compensationHistoryHeaders.modifiedBy}</TableHeader>
                            <TableHeader>{translate.compensationHistoryHeaders.compensationChangeDesc}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {compensationHistoryLogs
                            .filter((log) => log.serviceActivityId === serviceActivity.id)
                            .map((compensationHistoryLog) => (
                                <ServiceActivityCompensationPayoutsLogEntry
                                    compensationPayoutsLog={compensationHistoryLog}
                                    employmentId={employmentId}
                                    episodeCategory={props.episodeCategory}
                                    groupId={
                                        serviceGroupSettings.find(
                                            (sgs) =>
                                                compensationHistoryLog.serviceGroupSettingId ===
                                                sgs.serviceGroupSetting?.group.id
                                        )?.serviceGroupSetting?.group.id
                                    }
                                    key={`${compensationHistoryLog.id}`}
                                    organizationId={organizationId}
                                    serviceActivityName={serviceActivity.name}
                                    serviceName={serviceEmployments[0].service.name}
                                />
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    );

    async function getCompensationHistoryLogs() {
        const compensationPayoutsUrl = (entity: string, entityId: string, serviceActivityId: string) =>
            `/api/finance/compensationPayoutLogs?serviceActivityId=${serviceActivityId}&${entity}=${entityId}`;

        // on organization level we don't display the serviceEmployment compensation payout logs
        const serviceEmploymentCompensationPayoutLogs = employmentId
            ? await Promise.all(
                  serviceEmploymentIds.map((id) =>
                      abortableFetch.fetchJson(
                          compensationPayoutsUrl('serviceEmploymentId', id, serviceActivity.id),
                          'GET'
                      )
                  )
              )
            : [];

        const serviceOrganizationSettingsPayoutLogs = await Promise.all(
            serviceOrganizationSettingIds.map((id) =>
                abortableFetch.fetchJson(
                    compensationPayoutsUrl('serviceOrganizationSettingId', id, serviceActivity.id),
                    'GET'
                )
            )
        );

        const serviceGroupSettingsPayoutLogs = await Promise.all(
            serviceGroupSettingIds.map((serviceGroupSettingId) =>
                abortableFetch.fetchJson(
                    compensationPayoutsUrl('serviceGroupSettingId', serviceGroupSettingId!, serviceActivity.id),
                    'GET'
                )
            )
        );

        const servicesPayoutLogs = await abortableFetch.fetchJson(
            compensationPayoutsUrl('serviceId', serviceEmployments[0].service.id, serviceActivity.id),
            'GET'
        );

        const allCompensationPayouts = [
            ...serviceEmploymentCompensationPayoutLogs.map((response) => response.json),
            ...serviceOrganizationSettingsPayoutLogs.map((response) => response.json),
            ...serviceGroupSettingsPayoutLogs.map((response) => response.json),
            servicesPayoutLogs.json,
        ].flat();

        const logs = allCompensationPayouts.sort((a: CompensationPayoutLog, b: CompensationPayoutLog) => {
            return DateTime.from(b.created_at).diff(DateTime.from(a.created_at));
        });

        setCompensationHistoryLogs(logs);
    }
}
